import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchEmployeeCaseHistory,
  fetchEmployeeCaseReport,
  fetchEmployeeList,
} from "../../store/allocation/employeeDashboardSlice";
import { useEmployeeDashboardAccess } from "../../hooks/useEmployeeDashboardAccess";
import { DASHBOARD } from "../../components/employee-dashboard/dashboardTheme";
import {
  AdminEmployeeList,
  CustomEmployeeSelect,
  OverviewPeriodControls,
  type DatePreset,
  type PeriodSelection,
} from "../../components/employee-dashboard/DashboardFilterControls";
import {
  addDaysIso,
  currentOperationalDate,
  inferOperationalPreset,
  periodCaption,
} from "../../utils/operationalCalendar";
import {
  employeeRowBucket,
  type RedesignBucketFilter,
} from "../../utils/redesignBuckets";
import { RedesignBucketToggle } from "../../components/common/RedesignBucketToggle";
import {
  DailyCaseReportTable,
  EmployeeLifecycleTable,
  HistoryModal,
  OverviewSummaryCards,
  TotalWorkCard,
  employeeWorkSplit,
} from "../../components/employee-dashboard/EmployeeCaseReportPanels";
import {
  OverviewCharts,
  OverviewMixBar,
} from "../../components/employee-dashboard/OverviewCharts";

function availableYears() {
  const current = Number(currentOperationalDate().slice(0, 4));
  return [current - 2, current - 1, current];
}

function parseIsoDate(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function calendarToday() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

function defaultFilters() {
  const day = addDaysIso(calendarToday(), -2);
  return {
    year: Number(day.slice(0, 4)),
    startDate: day,
    endDate: day,
  };
}

const redesignPct = (count: number, designed: number) => {
  if (designed <= 0) return null;
  return Math.round((count / designed) * 1000) / 10;
};

const Overview = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { access, canViewAll, employee } = useEmployeeDashboardAccess();
  const {
    employees,
    caseReport,
    caseHistory,
    caseReportLoading,
    caseHistoryLoading,
    error,
  } = useAppSelector((state) => state.employeeDashboard);
  const [pickerReady, setPickerReady] = useState(false);
  const [datePreset, setDatePreset] = useState<DatePreset>("day");
  const [selectedDay, setSelectedDay] = useState<string | "all">("all");
  const [reasonFilter, setReasonFilter] = useState<RedesignBucketFilter>("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLiveId, setModalLiveId] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const defaults = useMemo(() => defaultFilters(), []);

  const filters = useMemo(() => {
    const yearParam = searchParams.get("year");
    const fromParam = searchParams.get("from");
    const toParam = searchParams.get("to");

    const year = yearParam ? Number(yearParam) : defaults.year;
    const startDate = fromParam || defaults.startDate;
    const endDate = toParam || defaults.endDate;

    const start = parseIsoDate(startDate);
    const end = parseIsoDate(endDate);
    if (!start || !end || end < start) {
      return defaults;
    }

    return { year, startDate, endDate };
  }, [searchParams, defaults]);

  const updateFilters = useCallback(
    (next: { year?: number; startDate?: string; endDate?: string; preset?: DatePreset }) => {
      const merged = { ...filters, ...next };
      const params: Record<string, string> = {};
      const empId = searchParams.get("empId");
      if (empId) params.empId = empId;
      params.year = String(merged.year);
      params.from = merged.startDate;
      params.to = merged.endDate;
      params.preset = next.preset ?? datePreset;
      setSearchParams(params);
    },
    [filters, datePreset, searchParams, setSearchParams]
  );

  const applyPeriod = (next: PeriodSelection) => {
    setDatePreset(next.preset);
    updateFilters({
      startDate: next.startDate,
      endDate: next.endDate,
      year: Number(next.startDate.slice(0, 4)),
      preset: next.preset,
    });
  };

  const setEmployeeId = useCallback(
    (empId: number | "") => {
      const params: Record<string, string> = {
        year: String(filters.year),
        from: filters.startDate,
        to: filters.endDate,
        preset: datePreset,
      };
      if (empId) params.empId = String(empId);
      setSearchParams(params);
    },
    [filters, datePreset, setSearchParams]
  );

  const empIdFromUrl = searchParams.get("empId")
    ? Number(searchParams.get("empId"))
    : null;

  const targetEmpId = useMemo(() => {
    if (canViewAll) return empIdFromUrl;
    return employee?.emp_id ?? null;
  }, [canViewAll, empIdFromUrl, employee?.emp_id]);

  useEffect(() => {
    if (searchParams.get("from") && searchParams.get("to")) return;
    const d = defaultFilters();
    const params: Record<string, string> = {
      year: String(d.year),
      from: d.startDate,
      to: d.endDate,
      preset: "day",
    };
    const empId = searchParams.get("empId");
    if (empId) params.empId = empId;
    setSearchParams(params, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const saved = searchParams.get("preset");
    if (saved === "day" || saved === "week" || saved === "month" || saved === "custom") {
      setDatePreset(saved);
      return;
    }
    const from = searchParams.get("from") || filters.startDate;
    const to = searchParams.get("to") || filters.endDate;
    setDatePreset(inferOperationalPreset(from, to));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!access || !canViewAll) return;
    dispatch(fetchEmployeeList({ ...access })).finally(() =>
      setPickerReady(true)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewAll, dispatch, access?.portalRole]);

  useEffect(() => {
    setSelectedDay("all");
  }, [targetEmpId, filters.startDate, filters.endDate]);

  useEffect(() => {
    if (!access || !targetEmpId) return;
    dispatch(
      fetchEmployeeCaseReport({
        ...access,
        empId: targetEmpId,
        startDate: filters.startDate,
        endDate: filters.endDate,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    targetEmpId,
    filters.startDate,
    filters.endDate,
    access?.portalRole,
    access?.viewerEmpId,
  ]);

  const showDashboard = !canViewAll || !!targetEmpId;
  const selectedEmployee = useMemo(
    () => employees.find((e) => e.emp_id === targetEmpId),
    [employees, targetEmpId]
  );

  const showingPicker = canViewAll && !targetEmpId;
  const displayName = showingPicker
    ? "Select Employee"
    : caseReport?.emp_name ??
      selectedEmployee?.emp_name ??
      employee?.emp_name ??
      "Employee Dashboard";
  const displayRole =
    caseReport?.role ?? selectedEmployee?.role ?? employee?.role ?? "";
  const displayShift =
    caseReport?.shift ?? selectedEmployee?.shift ?? employee?.shift ?? "";

  const rows = useMemo(() => {
    const all = caseReport?.rows || [];
    const inDay =
      selectedDay === "all"
        ? all
        : all.filter((row) => row.operational_date === selectedDay);
    if (reasonFilter === "all") return inDay;
    return inDay.filter((row) => employeeRowBucket(row) === reasonFilter);
  }, [caseReport?.rows, selectedDay, reasonFilter]);

  const blunderStats = useMemo(() => {
    const live = (caseReport?.rows || []).filter((row) => row.kind === "live");
    const blunderCount = live.filter(
      (row) => employeeRowBucket(row) === "blunder",
    ).length;
    const denominator =
      caseReport?.summary.tracked_cases ??
      caseReport?.summary.designed_cases ??
      live.length;
    return {
      blunderCount,
      blunderPct: redesignPct(blunderCount, denominator),
    };
  }, [
    caseReport?.rows,
    caseReport?.summary.tracked_cases,
    caseReport?.summary.designed_cases,
  ]);

  const scopeRows = useMemo(() => {
    const all = caseReport?.rows || [];
    if (selectedDay === "all") return all;
    return all.filter((row) => row.operational_date === selectedDay);
  }, [caseReport?.rows, selectedDay]);

  const workSplit = useMemo(
    () => employeeWorkSplit(scopeRows, caseReport?.emp_name),
    [scopeRows, caseReport?.emp_name],
  );

  const openReference = async (row: { live_case_id: string; has_reference: boolean; came_back: boolean }) => {
    if (!access || !targetEmpId) return;
    if (!row.has_reference && !row.came_back) return;
    setModalLiveId(row.live_case_id);
    setModalOpen(true);
    setHistoryError(null);
    try {
      await dispatch(
        fetchEmployeeCaseHistory({
          ...access,
          empId: targetEmpId,
          live_case_id: row.live_case_id,
        })
      ).unwrap();
    } catch (err: unknown) {
      setHistoryError(
        typeof err === "string" ? err : "Failed to load history"
      );
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalLiveId(null);
    setHistoryError(null);
  };

  const historyRows =
    caseHistory?.live_case_id === modalLiveId ? caseHistory.rows : [];
  const years = useMemo(() => availableYears(), []);
  const summary = caseReport?.summary;

  return (
    <>
      <Helmet>
        <title>{displayName} · Employee Dashboard</title>
      </Helmet>

      <div
        className={`min-h-full w-full min-w-0 overflow-x-hidden ${
          showingPicker ? "flex flex-1 flex-col" : ""
        }`}
        style={{ backgroundColor: DASHBOARD.bg }}
      >
        <div
          className={`flex flex-col gap-4 w-full min-w-0 ${
            showingPicker ? "flex-1" : ""
          }`}
        >
          {showingPicker ? (
            <>
              <div className="rounded-2xl border border-[#E8E8E4] bg-white p-4 shadow-sm">
                <OverviewPeriodControls
                  preset={datePreset}
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  years={years}
                  variant="light"
                  onChange={applyPeriod}
                />
              </div>
              <AdminEmployeeList
                team={employees}
                selectedId={targetEmpId}
                onSelect={setEmployeeId}
                loading={!pickerReady}
                fill
                grouped
                subtitle="Design team first. CRM and Admin are optional."
              />
            </>
          ) : null}

          {error ? (
            <div className="text-red-500 bg-white border border-red-200 rounded-2xl p-3">
              {error}
            </div>
          ) : null}

          {showDashboard ? (
            <>
              <div
                className="rounded-2xl px-4 py-5 md:px-8 md:py-6 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5 min-w-0 overflow-visible"
                style={{ backgroundColor: DASHBOARD.header }}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl shrink-0"
                    style={{ backgroundColor: DASHBOARD.teal }}
                  >
                    🦷
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white truncate">
                      {caseReportLoading && !caseReport ? "Loading…" : displayName}
                    </h1>
                    <p className="text-white/80 text-xs sm:text-sm mt-0.5 break-words">
                      {displayRole}
                      {displayShift ? ` · ${displayShift}` : ""} ·{" "}
                      {periodCaption(datePreset, filters.startDate, filters.endDate)}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full xl:w-auto min-w-0">
                  {canViewAll ? (
                    <CustomEmployeeSelect
                      team={employees}
                      value={targetEmpId ?? ""}
                      onChange={setEmployeeId}
                      disabled={!pickerReady}
                      variant="header"
                      className="w-full sm:w-[220px]"
                      grouped
                    />
                  ) : null}

                  <OverviewPeriodControls
                    preset={datePreset}
                    startDate={filters.startDate}
                    endDate={filters.endDate}
                    years={years}
                    variant="header"
                    onChange={applyPeriod}
                  />
                </div>
              </div>

              {caseReportLoading && !caseReport ? (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div
                        key={i}
                        className="h-[110px] animate-pulse rounded-2xl bg-white/60"
                      />
                    ))}
                  </div>
                  <div className="h-72 animate-pulse rounded-2xl bg-white/60" />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div className="h-64 animate-pulse rounded-2xl bg-white/60" />
                    <div className="h-64 animate-pulse rounded-2xl bg-white/60" />
                  </div>
                </>
              ) : summary ? (
                <>
                  <p className="text-sm text-[#4A4A48]">
                    {displayName} designed{" "}
                    <span className="font-semibold">{summary.designed_cases}</span>{" "}
                    live cases and QC'd{" "}
                    <span className="font-semibold">{summary.qc_cases ?? 0}</span>{" "}
                    in this {datePreset === "custom" ? "range" : datePreset}.{" "}
                    <span className="font-semibold">{summary.came_back}</span> of
                    the cases designed or QC'd came back (
                    <span className="font-semibold">
                      {summary.redesign_pct != null ? `${summary.redesign_pct}%` : "—"}
                    </span>{" "}
                    all redesign,{" "}
                    <span className="font-semibold">
                      {blunderStats.blunderPct != null
                        ? `${blunderStats.blunderPct}%`
                        : "—"}
                    </span>{" "}
                    blunder). A return counts for the designer and for QC2. Charts and rows
                    use noon-IST operational days; a later return still changes that
                    original day’s rate.
                  </p>
                  <TotalWorkCard
                    split={workSplit}
                    scopeLabel={
                      selectedDay === "all"
                        ? periodCaption(datePreset, filters.startDate, filters.endDate)
                        : `Operational day ${selectedDay}`
                    }
                  />

                  <OverviewSummaryCards
                    summary={summary}
                    blunderPct={blunderStats.blunderPct}
                    blunderCount={blunderStats.blunderCount}
                  />
                  <OverviewMixBar
                    summary={summary}
                    blunderCount={blunderStats.blunderCount}
                    blunderPct={blunderStats.blunderPct}
                  />
                  <OverviewCharts
                    days={caseReport?.days || []}
                    rows={caseReport?.rows || []}
                    empName={caseReport?.emp_name}
                    summary={summary}
                    selectedDate={selectedDay}
                    onSelectDate={setSelectedDay}
                  />
                  <DailyCaseReportTable
                    days={caseReport?.days || []}
                    selectedDate={selectedDay}
                    onSelectDate={setSelectedDay}
                  />

                  <div className="flex flex-wrap items-end justify-between gap-2">
                    <div>
                      <h2 className="text-sm font-semibold text-[#4A4A48]">
                        Case report
                      </h2>
                      <p className="mt-0.5 text-xs text-[#8A8A85]">
                        {selectedDay === "all"
                          ? `${periodCaption(datePreset, filters.startDate, filters.endDate)}. Cases designed or QC'd in that window.`
                          : `Operational day ${selectedDay} (12:00 IST → next noon). Click the day again to show the full ${datePreset}.`}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <RedesignBucketToggle
                        value={reasonFilter}
                        onChange={setReasonFilter}
                      />
                      {selectedDay !== "all" ? (
                        <button
                          type="button"
                          onClick={() => setSelectedDay("all")}
                          className="rounded-lg border border-[#E8E8E4] bg-white px-3 py-1.5 text-xs font-medium text-[#4A4A48] hover:border-[#345952]"
                        >
                          Show all days
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <EmployeeLifecycleTable
                    rows={rows}
                    empName={caseReport?.emp_name}
                    loading={caseReportLoading}
                    emptyMessage="No cases match this filter."
                    onOpenReference={openReference}
                    referenceLoadingId={
                      caseHistoryLoading ? modalLiveId : null
                    }
                  />
                </>
              ) : null}

              {modalOpen && modalLiveId ? (
                <HistoryModal
                  liveCaseId={modalLiveId}
                  rows={historyRows}
                  loading={caseHistoryLoading}
                  error={historyError}
                  onClose={closeModal}
                />
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default Overview;
